import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import FishboneDiagram from './FishboneDiagram';
import { useAlert } from '../context/AlertContext';

// `apiBase` lets the same modal serve both entity families:
// `${API}/incidents` (default — unchanged legacy behavior) or
// `${API}/observations` when opened from an observation detail page.
// `canExportPdf` hides the Export PDF button when the target backend has no
// fishbone export-pdf endpoint (observations, v1).
const FishboneAnalysis = ({ hazardId, onClose, apiBase = `${API}/incidents`, canExportPdf = true }) => {
  const { showSuccess, showError } = useAlert();
  const [showDiagram, setShowDiagram] = useState(false);
  const [problemStatement, setProblemStatement] = useState('');
  const [categories, setCategories] = useState({
    man: [''],
    machine: [''],
    method: [''],
    material: [''],
    measurement: [''],
    environment: ['']
  });
  const [rootCause, setRootCause] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const categoryInfo = {
    man: { name: 'Man (People)', icon: '👤', color: 'blue' },
    machine: { name: 'Machine (Equipment)', icon: '⚙️', color: 'purple' },
    method: { name: 'Method (Process)', icon: '📋', color: 'green' },
    material: { name: 'Material (Raw Materials)', icon: '📦', color: 'yellow' },
    measurement: { name: 'Measurement (Inspection)', icon: '📏', color: 'red' },
    environment: { name: 'Environment (Conditions)', icon: '🌡️', color: 'teal' }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [hazardId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiBase}/${hazardId}/rca/fishbone`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.problem_statement) {
        setProblemStatement(response.data.problem_statement);
        setCategories({
          man: response.data.man?.length > 0 ? response.data.man : [''],
          machine: response.data.machine?.length > 0 ? response.data.machine : [''],
          method: response.data.method?.length > 0 ? response.data.method : [''],
          material: response.data.material?.length > 0 ? response.data.material : [''],
          measurement: response.data.measurement?.length > 0 ? response.data.measurement : [''],
          environment: response.data.environment?.length > 0 ? response.data.environment : ['']
        });
        setRootCause(response.data.root_cause || '');
      }
    } catch (err) {
      console.error('Failed to load analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCauseChange = (category, index, value) => {
    const newCategories = { ...categories };
    newCategories[category][index] = value;
    setCategories(newCategories);
  };

  const addCause = (category) => {
    const newCategories = { ...categories };
    newCategories[category].push('');
    setCategories(newCategories);
  };

  const removeCause = (category, index) => {
    if (categories[category].length > 1) {
      const newCategories = { ...categories };
      newCategories[category] = newCategories[category].filter((_, i) => i !== index);
      setCategories(newCategories);
    }
  };

  const handleSave = async () => {
    if (!problemStatement.trim()) {
      showError('Please enter a problem statement');
      return;
    }

    setSaveLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiBase}/${hazardId}/rca/fishbone`,
        {
          hazard_id: hazardId,
          problem_statement: problemStatement,
          man: categories.man.filter(c => c.trim() !== ''),
          machine: categories.machine.filter(c => c.trim() !== ''),
          method: categories.method.filter(c => c.trim() !== ''),
          material: categories.material.filter(c => c.trim() !== ''),
          measurement: categories.measurement.filter(c => c.trim() !== ''),
          environment: categories.environment.filter(c => c.trim() !== ''),
          root_cause: rootCause,
          company_id: '',
          created_by: '',
          created_at: new Date().toISOString()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      showSuccess('Fishbone analysis saved successfully!');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to save analysis');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleExportPDF = async () => {
    console.log('Export PDF clicked');
    setExportLoading(true);

    try {
      const token = localStorage.getItem('token');
      console.log('Token retrieved:', token ? 'exists' : 'missing');

      // Check if analysis exists first
      console.log('Checking if analysis exists...');
      const checkResponse = await axios.get(
        `${apiBase}/${hazardId}/rca/fishbone`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Check response:', checkResponse.data);

      if (!checkResponse.data || !checkResponse.data.problem_statement) {
        console.log('No analysis found');
        showError('Please save the fishbone analysis before exporting to PDF.');
        setExportLoading(false);
        return;
      }

      console.log('Requesting PDF export...');
      const response = await axios.get(
        `${apiBase}/${hazardId}/rca/fishbone/export-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 30000
        }
      );

      console.log('PDF response received:', response.data.size, 'bytes');

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Fishbone_Analysis_${hazardId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);

      showSuccess('PDF exported successfully!');

    } catch (err) {
      console.error('Fishbone PDF export error:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        code: err.code
      });

      let errorMessage = 'Failed to export PDF. ';

      if (err.response?.status === 404) {
        errorMessage += 'Analysis not found. Please save first.';
      } else if (err.response?.status === 401) {
        errorMessage += 'Session expired. Please login again.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage += 'Request timed out. Please try again.';
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Unknown error occurred.';
      }

      showError(errorMessage);
    } finally {
      console.log('Export process complete');
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Wrap in error boundary
  try {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1B5C94]">Fishbone (Ishikawa) Diagram</h2>
          <p className="text-sm text-gray-600 mt-1">Analyze causes across 6 categories</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setShowDiagram(false)}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
            !showDiagram
              ? 'bg-[#1B5C94] text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Data
        </button>
        <button
          onClick={() => setShowDiagram(true)}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
            showDiagram
              ? 'bg-[#1B5C94] text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          View Diagram
        </button>
      </div>

      {/* Visual Fishbone Diagram */}
      {showDiagram && (
        <div className="mb-6">
          <FishboneDiagram
            problemStatement={problemStatement}
            categories={categories}
            rootCause={rootCause}
          />
          {(!problemStatement || problemStatement.trim() === '') && (
            <p className="text-center text-gray-500 mt-4 text-sm">
              Switch to "Data" tab to enter your fishbone analysis data
            </p>
          )}
        </div>
      )}

      {/* Data Entry Form */}
      {!showDiagram && (
        <>
      {/* Problem Statement */}
      <div className="mb-6">
        <label className="block text-sm font-bold text-gray-700 mb-2">
          Problem/Effect Statement <span className="text-red-500">*</span>
        </label>
        <textarea
          value={problemStatement}
          onChange={(e) => setProblemStatement(e.target.value)}
          rows="2"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
          placeholder="What is the problem or effect you are analyzing?"
        />
      </div>

      {/* 6 Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {Object.entries(categoryInfo).map(([key, info]) => {
          // Static color classes for Tailwind
          const categoryStyles = {
            blue: 'border-blue-200 bg-blue-50',
            purple: 'border-purple-200 bg-purple-50',
            green: 'border-green-200 bg-green-50',
            yellow: 'border-yellow-200 bg-yellow-50',
            red: 'border-red-200 bg-red-50',
            teal: 'border-teal-200 bg-teal-50'
          };
          
          const buttonStyles = {
            blue: 'bg-blue-200 text-blue-700 hover:bg-blue-300',
            purple: 'bg-purple-200 text-purple-700 hover:bg-purple-300',
            green: 'bg-green-200 text-green-700 hover:bg-green-300',
            yellow: 'bg-yellow-200 text-yellow-700 hover:bg-yellow-300',
            red: 'bg-red-200 text-red-700 hover:bg-red-300',
            teal: 'bg-teal-200 text-teal-700 hover:bg-teal-300'
          };
          
          return (
            <div key={key} className={`border-2 rounded-lg p-4 ${categoryStyles[info.color]}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <span className="text-2xl">{info.icon}</span>
                  {info.name}
                </h3>
                <button
                  onClick={() => addCause(key)}
                  className={`px-2 py-1 rounded text-sm font-semibold ${buttonStyles[info.color]}`}
                  title="Add cause"
                >
                  + Add
                </button>
              </div>
            
            <div className="space-y-2">
              {categories[key].map((cause, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={cause}
                    onChange={(e) => handleCauseChange(key, index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1B5C94] text-sm"
                    placeholder={`Cause related to ${info.name.split('(')[0].trim()}...`}
                  />
                  {categories[key].length > 1 && (
                    <button
                      onClick={() => removeCause(key, index)}
                      className="px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                      title="Remove"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          );
        })}
      </div>

      {/* Root Cause */}
      <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
        <label className="block text-sm font-bold text-gray-700 mb-2">
          Identified Root Cause
        </label>
        <textarea
          value={rootCause}
          onChange={(e) => setRootCause(e.target.value)}
          rows="3"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Based on the fishbone analysis, what is the primary root cause?"
        />
      </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex-1 bg-[#1B5C94] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#164A7B] transition-colors disabled:opacity-50"
        >
          {saveLoading ? 'Saving...' : 'Save Analysis'}
        </button>
        {canExportPdf && (
          <button
            onClick={handleExportPDF}
            disabled={exportLoading}
            className="px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {exportLoading ? 'Exporting...' : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export PDF
              </>
            )}
          </button>
        )}
        {onClose && (
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        )}
      </div>

      {/* Methodology Info */}
      <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4">
        <h4 className="font-bold text-blue-800 mb-2">🐟 Fishbone Methodology (6M)</h4>
        <p className="text-sm text-blue-700 mb-2">
          Also known as Ishikawa or Cause-and-Effect Diagram. Identify potential causes across 6 categories:
        </p>
        <ul className="text-sm text-blue-700 space-y-1 ml-4">
          <li><strong>Man:</strong> People-related causes (training, skills, behavior)</li>
          <li><strong>Machine:</strong> Equipment, tools, technology failures</li>
          <li><strong>Method:</strong> Processes, procedures, work methods</li>
          <li><strong>Material:</strong> Raw materials, supplies, components</li>
          <li><strong>Measurement:</strong> Inspection, data collection, monitoring</li>
          <li><strong>Environment:</strong> Working conditions, temperature, lighting</li>
        </ul>
      </div>
    </div>
    );
  } catch (renderError) {
    console.error('Fishbone component render error:', renderError);
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Fishbone Analysis</h2>
        <p className="text-gray-700">{renderError.message}</p>
        <button
          onClick={onClose}
          className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Close
        </button>
      </div>
    );
  }
};

export default FishboneAnalysis;
